import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmployeeAvatar } from '@/components/Shared/EmployeeAvatar';
import { usePlanningAssistantStore } from '@/store/usePlanningAssistantStore';
import { getObjectColors } from '@/utils/objectColors';
import {
  asDueCategory,
  employeeById,
  fitsForTask,
  fmtHours,
  fmtTime,
  isAvailable,
  notFitText,
  objectColorKey,
  objectMap,
  okFits,
  unplacedEmployeesForTask,
} from './assistantFormat';
import type { AssistantTask, DueCategory, SuggestionFit, SuggestionsDto } from './assistantTypes';
import { AssistantMap } from './components/AssistantMap';
import { CandidateCard } from './components/CandidateCard';
import { DueTag } from './components/DueTag';
import { ObjectTag } from './components/ObjectTag';

type DueFilter = 'all' | DueCategory;
const DUE_FILTERS: DueCategory[] = ['Day', 'Week', 'Month', 'None'];

interface Props {
  data: SuggestionsDto;
}

export function TaskMode({ data }: Props) {
  const { t, i18n } = useTranslation('planning');
  const lang = i18n.language;
  const selectedTaskId = usePlanningAssistantStore((s) => s.selectedTaskId);
  const selectTask = usePlanningAssistantStore((s) => s.selectTask);
  const assign = usePlanningAssistantStore((s) => s.assign);
  const busy = usePlanningAssistantStore((s) => s.busy);
  const newlyAssignedTaskIds = usePlanningAssistantStore((s) => s.newlyAssignedTaskIds);

  const [query, setQuery] = useState('');
  const [due, setDue] = useState<DueFilter>('all');
  const [hoverEmployeeId, setHoverEmployeeId] = useState<string | null>(null);

  const objects = useMemo(() => objectMap(data), [data]);
  const tasks = useMemo(() => data.tasks ?? [], [data]);

  const bestByTask = useMemo(() => {
    const best = new Map<string, SuggestionFit>();
    okFits(data).forEach((f) => {
      if (f.taskId && !best.has(f.taskId)) best.set(f.taskId, f);
    });
    return best;
  }, [data]);

  const counts = useMemo(() => {
    const c: Record<DueCategory, number> = { Day: 0, Week: 0, Month: 0, None: 0 };
    tasks.forEach((task) => {
      c[asDueCategory(task.dueCategory)] += 1;
    });
    return c;
  }, [tasks]);

  const q = query.trim().toLowerCase();
  const list = tasks.filter(
    (task) =>
      (due === 'all' || asDueCategory(task.dueCategory) === due) &&
      (!q ||
        [task.name, task.objectName, task.objectShortName, task.customerName]
          .filter(Boolean)
          .some((s) => (s as string).toLowerCase().includes(q))),
  );

  const task: AssistantTask | undefined = tasks.find((x) => x.taskId === selectedTaskId) ?? list[0];
  const fits = task ? fitsForTask(data, task.taskId ?? '') : [];
  const top = fits.slice(0, 3);
  const ghostFit = top.find((f) => f.employeeId === hoverEmployeeId) ?? top[0] ?? null;
  const ghostEmployee = employeeById(data, ghostFit?.employeeId);
  // Fits beyond the top 3, then everyone without a fit (reason derived from the employee).
  const others: { employeeId: string; fit: SuggestionFit | null }[] = task
    ? [
        ...fits.slice(3).map((fit) => ({ employeeId: fit.employeeId ?? '', fit })),
        ...unplacedEmployeesForTask(data, task.taskId ?? '').map((e) => ({ employeeId: e.employeeId ?? '', fit: null })),
      ]
    : [];
  const routes = (data.employees ?? [])
    .filter(isAvailable)
    .map((employee) => ({ employee, dim: !top.some((f) => f.employeeId === employee.employeeId) }));

  return (
    <div className="grid gap-4 lg:h-full min-h-0 grid-cols-1 lg:grid-cols-[minmax(300px,380px)_minmax(0,1fr)]">
      {/* Left: open tasks */}
      <div className="bg-white border border-slate-200 rounded-xl flex flex-col min-h-0 max-h-[420px] lg:max-h-none">
        <div className="p-3 flex flex-col gap-2 border-b border-slate-100">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200 text-[12px]">
            <Search size={13} strokeWidth={2} className="text-slate-400 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('assistant.taskMode.searchPlaceholder')}
              className="bg-transparent outline-none flex-1 min-w-0 placeholder:text-slate-400"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {(['all', ...DUE_FILTERS] as DueFilter[]).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDue(d)}
                className={`px-2 py-0.5 rounded-full border text-[11.5px] tabular-nums whitespace-nowrap ${
                  due === d
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {t(`assistant.taskMode.filters.${d}`)} {d === 'all' ? tasks.length : counts[d]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {tasks.length === 0 && (
            <div className="p-6 text-center text-[13px] text-slate-500">{t('assistant.taskMode.noTasks')}</div>
          )}
          {tasks.length > 0 && list.length === 0 && (
            <div className="p-6 text-center text-[13px] text-slate-500">{t('assistant.taskMode.noMatch')}</div>
          )}
          {list.map((x) => {
            const best = bestByTask.get(x.taskId ?? '');
            const bestEmployee = employeeById(data, best?.employeeId);
            const isActive = task?.taskId === x.taskId;
            const { color } = getObjectColors(objectColorKey(x.objectId, x.objectName));
            return (
              <button
                key={x.taskId}
                type="button"
                onClick={() => selectTask(x.taskId ?? null)}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 ${
                  isActive ? 'bg-slate-50' : 'hover:bg-slate-50'
                }`}
              >
                <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: color }} />
                <span className="flex-1 min-w-0">
                  <span
                    className={`block text-[13px] truncate ${isActive ? 'font-semibold text-slate-900' : 'text-slate-800'}`}
                  >
                    {x.name}
                  </span>
                  <span className="flex items-center gap-1.5 mt-0.5 min-w-0">
                    <span className="text-[11px] text-slate-500 tabular-nums">{fmtHours(x.durationMinutes, lang)}</span>
                    <DueTag due={x.dueCategory} />
                    <span className="text-[11px] text-slate-400 truncate">{x.objectShortName || x.objectName}</span>
                  </span>
                </span>
                {best && bestEmployee ? (
                  <span
                    className="flex items-center gap-1 shrink-0"
                    title={t('assistant.taskMode.bestSuggestion', { name: bestEmployee.name ?? '' })}
                  >
                    <EmployeeAvatar
                      employee={{ name: bestEmployee.name, avatarColor: bestEmployee.avatarColor }}
                      size={22}
                    />
                    <span className="text-[10.5px] tabular-nums text-slate-500 w-5 text-right">
                      {Math.round(best.score ?? 0)}
                    </span>
                  </span>
                ) : (
                  <span className="text-[10.5px] text-red-600 shrink-0">{t('assistant.taskMode.noPlace')}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500 text-center tabular-nums">
          {t('assistant.taskMode.footer', { count: tasks.length })}
        </div>
      </div>

      {/* Right: selected task */}
      {task ? (
        <div className="flex flex-col gap-4 min-h-0 lg:overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
            <span
              className="w-1 self-stretch rounded-full shrink-0"
              style={{ background: getObjectColors(objectColorKey(task.objectId, task.objectName)).color }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">
                {t('assistant.taskMode.kicker')}
              </div>
              <div className="text-[18px] font-semibold tracking-tight text-slate-900">{task.name}</div>
              <div className="flex items-center gap-2 mt-1.5 text-[12px] text-slate-500 flex-wrap">
                <ObjectTag objectId={task.objectId} objectName={task.objectName} label={task.objectShortName} size="md" />
                {task.objectAddress && <span>{task.objectAddress}</span>}
                {task.customerName && (
                  <>
                    <span>·</span>
                    <span>{task.customerName}</span>
                  </>
                )}
                <span>·</span>
                <span className="tabular-nums">{fmtHours(task.durationMinutes, lang)}</span>
                <DueTag due={task.dueCategory} />
              </div>
            </div>
          </div>

          {top.length > 0 ? (
            <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
              {top.map((f, i) => {
                const employee = employeeById(data, f.employeeId);
                if (!employee) return null;
                return (
                  <CandidateCard
                    key={f.employeeId}
                    fit={f}
                    task={task}
                    employee={employee}
                    rank={i}
                    objects={objects}
                    active={ghostFit?.employeeId === f.employeeId}
                    disabled={busy}
                    newlyAssignedTaskIds={newlyAssignedTaskIds}
                    onHover={setHoverEmployeeId}
                    onAssign={(fit) => void assign(fit)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-6 text-center text-[13px] text-slate-500">
              {t('assistant.taskMode.nobody')}
            </div>
          )}

          <div className="grid gap-4 flex-1 min-h-[300px] grid-cols-1 xl:grid-cols-[minmax(0,1fr)_260px]">
            <AssistantMap
              className="min-h-[300px]"
              routes={routes}
              markers={[{ task, rank: null }]}
              objects={objects}
              selectedTaskId={task.taskId}
              ghost={ghostFit && ghostEmployee ? { fit: ghostFit, task, employee: ghostEmployee } : null}
            />
            <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col gap-1 min-h-0 overflow-y-auto">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 px-1 pb-1">
                {t('assistant.taskMode.others')}
              </div>
              {others.map(({ employeeId, fit: f }) => {
                const employee = employeeById(data, employeeId);
                if (!employee) return null;
                return (
                  <div key={employeeId} className="flex items-center gap-2 px-1 py-1.5 border-t border-slate-100">
                    <EmployeeAvatar employee={{ name: employee.name, avatarColor: employee.avatarColor }} size={22} />
                    <span className="text-[12px] text-slate-700 flex-1 truncate">{employee.name}</span>
                    {f ? (
                      <button
                        type="button"
                        onClick={() => void assign(f)}
                        disabled={busy}
                        title={t('assistant.assign')}
                        className="text-[11px] tabular-nums text-slate-500 hover:text-emerald-700 disabled:opacity-50"
                      >
                        {Math.round(f.score ?? 0)} · {fmtTime(f.start)}
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-400 text-right">{notFitText(t, employee, lang)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-6 text-center text-[13px] text-slate-500 self-start">
          {t('assistant.taskMode.noTasks')}
        </div>
      )}
    </div>
  );
}
